/**
 * Export Command
 *
 * Export vault credentials for backup or transfer.
 */

import * as fs from 'fs-extra';
import { CredentialService } from '../core/service';
import { loadConfig } from '../config/loader';
import * as output from '../utils/output';
import { confirm } from '../utils/prompts';

interface ExportOptions {
  output?: string;
  format?: 'json' | 'env';
  keys?: string;
  encrypted?: boolean;
  quiet?: boolean;
  yes?: boolean;
}

export async function exportCommand(options: ExportOptions): Promise<void> {
  const service = new CredentialService();

  // Check vault exists
  const vaults = await service.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  await service.init();

  const credentials = await service.list();
  if (credentials.length === 0) {
    output.warning('No credentials in vault to export.');
    process.exit(0);
  }

  let exportContent: string;
  const format = options.format || 'json';

  if (options.encrypted) {
    // Export encrypted vault
    const vaultPath = await service.getVaultPath();
    const vaultFile = `${vaultPath}/vault.json`;

    if (await fs.pathExists(vaultFile)) {
      const vaultContent = await fs.readFile(vaultFile, 'utf8');
      let vaultData = JSON.parse(vaultContent);

      // Filter by keys if specified
      if (options.keys) {
        const keysToExport = options.keys.split(',').map((k) => k.trim());
        vaultData = vaultData.filter((entry: { name: string }) => keysToExport.includes(entry.name));

        // Warn about missing keys
        const foundNames = new Set(vaultData.map((e: { name: string }) => e.name));
        const missingKeys = keysToExport.filter((k) => !foundNames.has(k));
        if (missingKeys.length > 0 && !options.quiet) {
          output.warning(`Keys not found: ${missingKeys.join(', ')}`);
        }

        if (vaultData.length === 0) {
          output.error('No matching credentials found.');
          process.exit(1);
        }
      }

      exportContent = JSON.stringify(vaultData, null, 2) + '\n';
    } else {
      output.error('Vault file not found.');
      process.exit(1);
    }

    if (!options.quiet && !options.output) {
      output.warning('Exporting encrypted vault (credentials remain encrypted)');
    }
  } else {
    // Decrypt and export - requires confirmation for security
    const config = await loadConfig();
    const trustedMode = config.agent?.trustedMode === true;

    // Security: require interactive confirmation unless trustedMode is enabled
    if (!trustedMode) {
      if (options.yes) {
        output.error('--yes flag requires agent.trustedMode: true in config.');
        output.info('This protects against agents exfiltrating credentials.');
        output.info('Run: secretsage config --set agent.trustedMode=true');
        process.exit(1);
      }

      // Force interactive confirmation
      output.warning('⚠️  SECURITY WARNING: This will expose ALL decrypted credentials.');
      const confirmed = await confirm('Export all credentials? This cannot be undone.');
      if (!confirmed) {
        output.info('Export cancelled.');
        process.exit(0);
      }
    }

    const allDecrypted = await service.getAll();

    // Filter by keys if specified
    let decrypted = allDecrypted;
    if (options.keys) {
      const keysToExport = options.keys.split(',').map((k) => k.trim());
      decrypted = allDecrypted.filter((c) => keysToExport.includes(c.name));

      // Warn about missing keys
      const foundNames = new Set(decrypted.map((c) => c.name));
      const missingKeys = keysToExport.filter((k) => !foundNames.has(k));
      if (missingKeys.length > 0 && !options.quiet) {
        output.warning(`Keys not found: ${missingKeys.join(', ')}`);
      }

      if (decrypted.length === 0) {
        output.error('No matching credentials found.');
        process.exit(1);
      }
    }

    if (!options.quiet && !options.output) {
      output.warning('Exporting DECRYPTED credentials. Handle with care!');
    }

    if (format === 'env') {
      // Export as .env format
      exportContent = decrypted
        .map((c) => {
          const needsQuotes = /[\s#"'\\]/.test(c.value) || c.value.includes('=');
          const quotedValue = needsQuotes ? `"${c.value.replace(/"/g, '\\"')}"` : c.value;
          return `${c.name}=${quotedValue}`;
        })
        .join('\n') + '\n';
    } else {
      // Export as JSON
      exportContent = JSON.stringify(
        decrypted.map((c) => ({
          name: c.name,
          value: c.value,
          createdAt: c.metadata?.createdAt,
          updatedAt: c.metadata?.updatedAt,
        })),
        null,
        2
      ) + '\n';
    }
  }

  // Output
  if (options.output) {
    await fs.writeFile(options.output, exportContent, { mode: 0o600 });
    if (!options.quiet) {
      output.success(`Exported ${credentials.length} credentials to ${options.output}`);
    }
  } else {
    // Write to stdout
    process.stdout.write(exportContent);
  }
}
